import React, { useState } from 'react';
import { Produit } from '../services/apiProduits';
import { Title } from 'components/base/Title';
import { parseToCurrency, usePanier } from 'context/panier-context';
import { Button } from 'components/base/Button';
import { Card } from 'components/base/Card';
import { Text } from 'components/base/Text';
import { Input } from 'components/base/Input';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import Section from 'components/base/Section';
import { LivraisonBanniere } from 'components/LivraisonBanniere';
import { paiement } from 'services/apiPanier';
import { handleApiError } from 'utils/errorHandlers';
import { ApiLoading } from 'components/ApiLoading';

export const Panier: React.FC = () => {
	const navigate = useNavigate();
	const { panier, stockModifies, loading, removeFromPanier, handleQuantityChange } = usePanier(); // Accès au panier
	const [errors, setErrors] = useState<Record<string, string[]>>({});

	// Afficher un loader pendant le chargement
	if (loading) {
		return (
			<Section fullwidth>
				<div className="min-h-container flex flex-col items-center justify-center text-center gap-4 -my-4">
					<Title type='display' centered className='mb-4'>Votre panier</Title>
					<Title type='h2' className='animate-pulse'>Chargement...</Title>
				</div>
			</Section>
		);
	}

	const total = panier?.produits.reduce(
		(sum: number, p: Produit) => sum + p.prix_num * p.quantite,
		0
	) ?? 0;

	const handleCommander = async () => {
		if (!panier) return;
		navigate('/paiement');
		// try {
		// 	const response = await paiement(panier);
		// 	if (response.status === 'success') {
		// 		alert("redirection stripe");
		// 	} else {
		// 		setErrors(handleApiError(response.message));
		// 	}
		// } catch (error) {
		// 	console.error(error);
		// }
	}

	if (errors && errors.general) {
		return <ApiLoading type="error">{errors.general[0]}</ApiLoading>;
	}

	return (
		!panier || panier.produits.length === 0
			?
			<Section fullwidth>
				<div
					className="min-h-container flex flex-col items-center justify-center text-center gap-4 -my-4"
					style={{
						backgroundImage: 'url(/bg-error.JPG)',
						backgroundRepeat: "no-repeat",
						backgroundSize: "cover"
					}}
				>
					<div className='absolute inset-0 bg-gray-600 opacity-30' />

					<Card className="p-8 flex flex-col items-center justify-center z-10 shadow-sm shadow-primary-100">
						{/* Logo et nom du site */}
						<div className="flex items-center gap-4 mb-6">
							<img src="/empty-cart.png" alt="Image panier vide" className="w-16" />
							<Title type="h1" className="text-4xl font-bold text-primary">Votre panier est vide</Title>
						</div>

						<Title type="h6" className="mb-6">Vous n'avez pas encore ajouté de produits à votre panier.</Title>
						<Button onClick={() => navigate("/produits")}>Voir les produits</Button>

					</Card>
				</div>
			</Section>
			:
			<Section>
				<LivraisonBanniere className='mb-4' />

				<Title type='display' centered className='mb-4'>Votre panier</Title>

				{/* Table en pc */}
				<div className="hidden lg:block mb-2">
					<table className="w-full">
						<thead>
							<tr>
								<th></th>
								<th className="px-4 py-2 text-center">PU</th>
								<th className="px-4 py-2 text-center">Qté</th>
								<th className="px-4 py-2 text-center">Total</th>
								<th className='w-16'></th>
							</tr>
						</thead>
						<tbody>
							{panier?.produits.map((produit, index) => {
								const isAjuste = stockModifies.produits_ajustes.some(stockModifie => stockModifie.produit_id === produit.id);
								const stockModifie = stockModifies.produits_ajustes.find(stockModifie => stockModifie.produit_id === produit.id);
								return (
									<>
										<tr key={index} className="border-t">
											<td className="px-4 py-2 flex items-center gap-2">
												<img src={produit.image_url ?? "/produits/ete-500.jpg"}
													className='shadow rounded-full w-20 h-20 object-cover flex-shrink-0'
													alt={`image du produit ${produit.libelle}`} />
												<div className='flex flex-col md:flex-row w-full justify-between'>
													<div>
														<Title type='h5'>{produit.libelle}</Title>
														<Text>{produit.description}</Text>
													</div>
												</div>
											</td>
											<td className="px-4 py-2 text-center">{parseToCurrency(produit.prix_num)}</td>
											<td className="px-4 py-2 text-center">
												<Input
													width={4}
													type="number"
													min={1}
													value={produit.quantite}
													max={produit.stockDisponible}
													onChange={e =>
														handleQuantityChange(produit.id, Number(e.target.value), produit.stockDisponible)
													}
													className="leading-none text-center"
												/>
											</td>
											<td className="px-4 py-2 text-center">{parseToCurrency(produit.prix_num * produit.quantite)}</td>
											<td className="px-4 py-2 text-center">
												<Button
													variant="alert-outline"
													icon={<TrashIcon />}
													onClick={() => removeFromPanier(produit.id)}
												>
												</Button>
											</td>
										</tr>
										{isAjuste && stockModifie && (
											<tr key={index + 'ajuste'}>
												<td colSpan={5} className="px-4 py-2">
													<div className='flex items-center gap-2 border border-alert-500 bg-alert-100 rounded-md p-2'>
														<Text><span className='font-semibold'>{produit.libelle}</span> n'a pas assez de stock disponible (quantité demandée : {stockModifie.quantite_demandee})</Text>
													</div>
												</td>
											</tr>
										)}
									</>
								)
							})}
							{stockModifies.produits_retires.map((stockModifie, index) => (
								<tr key={index} className="border-t">
									<td colSpan={5} className="px-4 py-2">
										<div className='flex items-center gap-2 border border-alert-500 bg-alert-100 rounded-md p-2'>
											<Text>Le produit <span className='font-semibold'>{stockModifie.nom}</span> n'a plus de stock disponible  (quantité demandée : {stockModifie.quantite_demandee})</Text>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<div className='flex lg:hidden flex-col gap-2'>
					{panier?.produits.map((produit, index) => {
						const isAjuste = stockModifies.produits_ajustes.some(stockModifie => stockModifie.produit_id === produit.id);
						const stockModifie = stockModifies.produits_ajustes.find(stockModifie => stockModifie.produit_id === produit.id);
						return (
							<Card className='flex flex-col gap-2' key={index}>
								<div className='flex gap-4'>
									<img src={produit.image_url ?? "/produits/ete-500.jpg"}
										className='shadow rounded-full w-20 h-20 object-cover flex-shrink-0'
										alt={`image du produit ${produit.libelle}`} />
									<div className='flex flex-col w-full justify-between'>
										<div className='flex justify-between gap-2'>
											<div>

												<Title type='h5'>{produit.libelle}</Title>
												<Text>{produit.description}</Text>
											</div>
											<div>
												<Button
													variant="alert-outline"
													icon={<TrashIcon />}
													onClick={() => removeFromPanier(produit.id)}
												>
												</Button>
											</div>
										</div>
										<Text paragraph className='mt-2 pt-2 border-t'>PU : {parseToCurrency(produit.prix_num)}</Text>
										<div className='flex gap-2 items-center'>
											<span>Qté : </span>
											<Input
												width={4}
												type="number"
												min={1}
												value={produit.quantite}
												max={produit.stockDisponible}
												onChange={e =>
													handleQuantityChange(produit.id, Number(e.target.value), produit.stockDisponible)
												}
												className="leading-none text-center"
											/>
										</div>
										<Text paragraph>Total : {parseToCurrency(produit.prix_num * produit.quantite)}</Text>
									</div>
								</div>
								{isAjuste && stockModifie && (
									<div className='border border-alert-500 bg-alert-100 rounded-md p-2'>
										<Text>Stock insuffisant (quantité demandée : {stockModifie.quantite_demandee})</Text>
									</div>
								)}
							</Card>
						)
					})}
					{stockModifies.produits_retires.map((stockModifie, index) => (
						<div key={index} className='border border-alert-500 bg-alert-100 rounded-md p-2'>
							<Text>Le produit <span className='font-semibold'>{stockModifie.nom}</span> n'a plus de stock disponible  (quantité demandée : {stockModifie.quantite_demandee})</Text>
						</div>
					))}
				</div>


				<Card className='bg-transparent border-0'>
					<div className='flex justify-between lg:pl-20'>
						<Title type='h5' bold>Total de la commande</Title>
						<Title type='h5' bold>{parseToCurrency(total)}</Title>
					</div>
					<div className='flex justify-end mt-4'>
						<Button variant="primary" onClick={handleCommander}>
							Commander
						</Button>
					</div>
				</Card>

			</Section>
	);
};